<template data-server-rendered="true">
  <div id="app" data-clarity-unmask="True">
    <component :is="Header"></component>
    <component :is="ButtonDashboardBottom" class="button-dashboard-bottom"></component>

    <div id="main-content" class="container cmain">
      <router-view v-slot="{ Component, route }">
        <Transition name="fade">
          <div :key="route.fullPath">
            <component :is="Component" />
          </div>
        </Transition>
      </router-view>
    </div>

    <component :is="Footer"></component>

    <b-modal v-model="$store.state.modal.premium.open" aria-role="dialog" aria-label="プレミアムプラン" aria-modal>
      <template #default="props">
        <Subscription :title="$store.state.modal.premium.title" :description="$store.state.modal.premium.description"
          @close="props.close" class="my-modal"></Subscription>
      </template>
    </b-modal>

    <b-modal v-model="$store.state.modal.relogin.open" aria-role="dialog" aria-label="再ログインのお願い" aria-modal>
      <template #default="props">
        <div @close="props.close" class="my-modal">
          <h3>
            <span class="section-title">再ログインが必要です</span>
          </h3>
          <div class="has-text-weight-bold margin-bottom-30px">
            <p>トークンの有効期限が切れました。再度ログインしてください。</p>
          </div>
          <ButtonLogout />
        </div>
      </template>
    </b-modal>

    <b-modal v-model="$store.state.modal.rateLimited.open" aria-role="dialog" :aria-label="$t('rateLimited.title')"
      aria-modal>
      <template #default="props">
        <div class="my-modal">
          <h3>
            <span class="section-title">{{ $t('rateLimited.title') }}</span>
          </h3>
          <div class="has-text-weight-bold margin-bottom-30px">
            <p>{{ $t('rateLimited.description') }}</p>
            <br>
            <p>{{ $t('rateLimited.waitMessage') }}</p>
          </div>
          <b-button type="is-primary" @click="CloseRateLimitModal(); props.close()" class="has-text-weight-bold"
            rounded>
            {{ $t('rateLimited.close') }}
          </b-button>
        </div>
      </template>
    </b-modal>

    <teleport to="head">
      <component :is="'script'" type="application/ld+json">
        {{ jsonld }}
      </component>
    </teleport>
  </div>
</template>

<script>
import appConfig from './app.config'
import ButtonSignUp from "./components/ButtonSignUp.vue";
import ButtonLogout from "./components/ButtonLogout.vue";
import ButtonLogin from "./components/ButtonLogin.vue";
import ButtonDashboard from "./components/ButtonDashboard.vue";
import Footer from "./components/Footer.vue";
import Header from "./components/Header.vue";
import EnhancedNavbar from "./components/navbar/EnhancedNavbar.vue";
import ButtonDashboardBottom from "./components/ButtonDashboardBottom.vue";
import { defineAsyncComponent } from "vue";
import { useRegisterSW } from 'virtual:pwa-register/vue'
import { useTheme } from './composables/useTheme'
import { usePostHog } from './composables/usePostHog'

// https://vite-pwa-org.netlify.app/guide/periodic-sw-updates.html
const intervalMS = 300000 // 5 minutes
const updateServiceWorker = useRegisterSW({
  onRegistered(r) {
    r && setInterval(() => {
      r.update()
    }, intervalMS)
  }
})

import { useFaro } from './helpers/useFaro'
import { LogLevel } from '@grafana/faro-web-sdk';

// テーマ管理のグローバルインスタンス
const themeInstance = useTheme();

export default {
  name: 'HStorage',
  computed: {
    ButtonDashboardBottom() {
      return ButtonDashboardBottom
    },
    Header() {
      return EnhancedNavbar
    },
    Footer() {
      return Footer
    }
  },
  components: {
    Header,
    EnhancedNavbar,
    ButtonDashboard,
    ButtonLogout,
    ButtonSignUp,
    ButtonLogin,
    Footer,
    Subscription: defineAsyncComponent(() => import('./components/SubscriptionComponent.vue'))
  },
  data() {
    return {
      jsonld: {}, // watch で更新する
      isToggled: false,
      faro: null,

      posthog: null, // PostHog instance
    }
  },
  watch: {
    '$route.name': function (to, _) {
      this.jsonld = appConfig.jsonld[to]
    },
  },
  async created() {
    // テーマの初期化
    themeInstance.initializeTheme();

    // PostHogの初期化
    const { posthog } = usePostHog();
    this.posthog = posthog;

    try {
      this.faro = useFaro();
    } catch (e) {
      console.error('Faro initialization error:', e);
    }

    this.$axios.get('/init', {})
      .then(response => {
        this.$store.state.init = response.data
        this.$store.state.uploadSetting.maxLargeTotalSize = response.data.setting['max_upload_size']
      })
      .catch(err => {
        this.toastError(this.$t('error.error'))
        throw new Error(err)
      })

    try {
      if (window.location.search.includes('code=') && window.location.search.includes('state=')) {
        const { appState } = await this.$auth0.handleRedirectCallback()
        onRedirectCallback(appState)
      }
    } catch (e) {
      this.error = e
    } finally {
      this.$auth0.getAccessTokenSilently()
        .then(async (token) => {
          this.$store.state.user = this.$auth0.user.value
          this.$store.state.token = token
          this.$store.state.isLoggedIn = this.$auth0.isAuthenticated.value

          if (this.$store.state.user.sub.indexOf('twitter') !== -1 || this.$store.state.user.sub.indexOf('google-oauth2') !== -1 || this.$store.state.user.sub.indexOf('line') !== -1) {
            this.$store.state.email_verified = true
          } else {
            this.$store.state.email_verified = this.$store.state.user.email_verified
          }

          this.$store.state.email = this.$store.state.user.email

          // Twitter は Auth0 Actions によって、user_metadata に入ってくる
          if (this.$store.state.user.sub.indexOf('twitter') !== -1) {
            const userMetadata = this.$store.state.user['https://hstorage.io/user_metadata']
            this.$store.state.email = userMetadata.twitter_email
            this.$store.state.user.email = userMetadata.twitter_email
          }

          // Faroが初期化されている場合のみユーザー情報を設定
          if (this.faro) {
            this.faro.api.setUser({
              email: this.$store.state.user.email,
              id: this.$store.state.user.sub,
            });
          }



          // PostHogにユーザー情報を設定
          if (this.posthog) {
            // ユーザーを識別
            this.posthog.identify(this.$store.state.user.sub, {
              email: this.$store.state.user.email,
              email_verified: this.$store.state.email_verified,
            });
          }

          const roles = this.$store.state.user['https://hstorage.io/roles']
          let userPlan = 'free'; // デフォルトプラン
          
          if (typeof roles !== 'undefined') {
            if (roles.indexOf('is_premium') !== -1) {
              this.$store.state.isPremium = true
              this.$store.state.isFree = false
              userPlan = 'premium';
            }
            if (roles.indexOf('is_business') !== -1) {
              this.$store.state.isBusiness = true
              this.$store.state.isFree = false
              userPlan = 'business';
            }
            if (roles.indexOf('api') !== -1) {
              this.$store.state.isAPIUser = true
            }
            if (roles.indexOf('is_onetime') !== -1) {
              this.$store.state.isOnetimeUser = true
              this.$store.state.isFree = false
              userPlan = 'onetime';
            }
          }

          // PostHogにプラン情報を設定
          if (this.posthog) {
            this.posthog.people.set({
              plan: userPlan,
              is_api_user: this.$store.state.isAPIUser || false,
              roles: roles || [],
            });
          }

          try {
            const res = await this.$axios.get(`/user`, {
              headers: {
                Authorization: `Bearer ${this.$store.state.token}`
              }
            })
            this.$store.commit('updateLoginInit', res.data)
            if (res.data['need_link']) {
              this.$router.push(`/${this.$i18n.locale}/account_linking`).catch(() => {
              })
            }
          } catch (err) {
            switch (err.response.status) {
              case 0:
                this.logout()
                break
              case 401:
                this.logout()
                break
              case 403:
                this.logout()
                break
              case 404:
                this.logout()
                break
              case 500:
                this.logout()
                break
              default:
                this.$router.push({ name: 'Error', query: { title: 'エラー' } }).catch(() => {
                })
                break
            }
          } finally {
            this.$store.state.isLoginProcessing = false
          }
        }).catch((e) => {
          if (e.error === 'missing_refresh_token' || e.error === 'invalid_grant') {
            this.logout()
          }
        }).finally(() => {
          this.$store.state.isLoginProcessing = false
        })

      try {
        // プレミアム、ビジネス、またはワンタイムユーザーの場合、広告を非表示にする
        if (this.$store.state.isPremium || this.$store.state.isBusiness || this.$store.state.isOnetimeUser) {
          HideAds()
        }
      } catch (e) {
        // Faroが初期化されている場合のみエラーをプッシュ
        if (this.faro) {
          this.faro.api.pushError(e);
        }
      }
    }
  },
  methods: {
  }
}
</script>

<style lang="scss">
// 1) Bulma 1.x の Sass モジュールを設定付きで読み込む（@use は最上部）
/* 1) Bulma initial variables: use a deterministic font for CI/local parity */
@use "bulma/sass/utilities/initial-variables" as * with (
  $family-sans-serif: ("Noto Sans JP", "Noto Sans", "Liberation Sans", "Arial", "Roboto", "Helvetica Neue", "Segoe UI", "-apple-system", "BlinkMacSystemFont", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji")
);
/* 2) Bulma utilities: brand colors only */
@use "bulma/sass/utilities" as bulma-util with (
  $primary: #F24368,
  $info: #222f3d,
);
@use "bulma/sass";
@use "bulma/sass/utilities/functions" as bulma-fn;

// 2) Bulma 0.9 系で存在していた関数名への互換レイヤ（Buefy の SCSS が参照）
@function findColorInvert($color) {
  @return bulma-fn.bulmaFindColorInvert($color);
}

@function findLightColor($color, $l: 96%) {
  @return bulma-fn.bulmaFindLightColor($color, $l);
}

@function findDarkColor($color, $base-l: 29%) {
  @return bulma-fn.bulmaFindDarkColor($color, $base-l);
}

// 3) アプリ側で使う基準色・フォント（Buefy の !default を上書きするためグローバルにも定義）
$primary: #F24368;
$info: #222f3d;

// 4) リンク色の調整（Bulma 1.x は $link-* invert 変数が無いので必要最低限のみ）
$link: $primary;
$link-focus-border: $primary;

  // snackbar
  $snackbar-border-radius: 16px;

  /* Snackbar visual refresh */
  .notices .snackbar {
    background: rgba(255, 255, 255, 0.96);
    color: #222f3d;
    border: 1px solid rgba(34, 47, 61, 0.08);
    border-radius: 16px;
    box-shadow: 0 10px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06);
    padding: 16px 18px;
    backdrop-filter: saturate(160%) blur(8px);
    justify-content: center; /* 中央寄せ */
    align-items: center;
  }
  .notices .snackbar .text {
    padding: 0; /* 既定の重複パディングを抑制 */
    font-size: 0.98rem;
    line-height: 1.5;
  }

  /* PC 版限定の横幅制御 */
  @media (min-width: 1024px) {
    .notices .snackbar {
      max-width: 450px !important; /* Buefy 既定(600px)を上書き */
      width: 100%;
    }
  }

:root,
.theme-light {
  /* Light theme (default) */
  --primary-color: #F24368;
  --background-gradient: linear-gradient(135deg, rgba(255, 252, 252, 0.97) 0%, rgba(250, 245, 250, 0.97) 50%, rgba(245, 247, 252, 0.97) 100%);
  --surface-color: #ffffff;
  --text-color: rgba(66, 82, 110, 1);
  --title-color: #18181B;
  --navbar-color: #222f3d;
  --card-background: #ffffff;
  --border-color: #e0e0e0;
  --modal-background: #ffffff;
  --dropdown-background: #ffffff;
  --dropdown-item-hover: #f5f5f5;
  --button-text-color: #ffffff;
  --navbar-link-color: #ffffff;
  --navbar-link-hover-color: #000000;
  --box-shadow: rgba(0, 0, 0, 0.1);

  /* Navbar glassmorphism and enhanced styling */
  --navbar-blur-strength: 10px;
  --navbar-bg-opacity: 0.85;
  --navbar-gradient: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%);
  --navbar-shadow: 0 2px 20px rgba(0, 0, 0, 0.08);
  --navbar-border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  --navbar-scrolled-bg: rgba(255, 255, 255, 0.98);
  --navbar-hover-transform: translateY(-2px);
  --navbar-active-indicator: #F24368;
  --navbar-dropdown-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  --navbar-mobile-overlay: rgba(0, 0, 0, 0.4);
  --navbar-item-transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  --navbar-height: 72px;
  --navbar-mobile-height: 64px;

  /* For compatibility */
  --primary-color: #F24368;
  --title-font-color: var(--title-color);
  --default-font-color: var(--text-color);
}

:root.theme-dark {
  /* Dark theme overrides */
  --background-gradient: linear-gradient(135deg, rgba(18, 18, 27, 0.97) 0%, rgba(25, 20, 25, 0.97) 50%, rgba(20, 22, 27, 0.97) 100%);
  --surface-color: #1a1a1a;
  --text-color: rgba(200, 200, 200, 1);
  --title-color: #ffffff;
  --navbar-color: #2a2a2a;
  --card-background: #2a2a2a;
  --border-color: #404040;
  --modal-background: #2a2a2a;
  --dropdown-background: #2a2a2a;
  --dropdown-item-hover: #3a3a3a;
  --button-text-color: #ffffff;
  --navbar-link-color: #ffffff;
  --navbar-link-hover-color: #cccccc;
  --box-shadow: rgba(0, 0, 0, 0.3);

  /* Navbar glassmorphism and enhanced styling for dark theme */
  --navbar-blur-strength: 12px;
  --navbar-bg-opacity: 0.8;
  --navbar-gradient: linear-gradient(135deg, rgba(42, 42, 42, 0.9) 0%, rgba(26, 26, 26, 0.85) 100%);
  --navbar-shadow: 0 2px 20px rgba(0, 0, 0, 0.25);
  --navbar-border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  --navbar-scrolled-bg: rgba(26, 26, 26, 0.96);
  --navbar-hover-transform: translateY(-2px);
  --navbar-active-indicator: #F24368;
  --navbar-dropdown-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  --navbar-mobile-overlay: rgba(0, 0, 0, 0.6);
  --navbar-item-transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  --navbar-height: 72px;
  --navbar-mobile-height: 64px;

  /* For compatibility */
  --title-font-color: var(--title-color);
  --default-font-color: var(--text-color);
}

/* Buefy コンポーネントのダークモード対応 */
.theme-dark {
  .dropdown-content {
    background-color: var(--dropdown-background);
    color: var(--text-color);
  }

  .dropdown-item {
    color: var(--text-color);

    &:hover {
      background-color: var(--dropdown-item-hover);
      color: var(--text-color);
    }
  }

  .modal-card {
    background-color: var(--modal-background);
    color: var(--text-color);
  }

  .modal-card-head,
  .modal-card-foot {
    background-color: var(--surface-color);
    border-color: var(--border-color);
  }

  .modal-card-title {
    color: var(--title-color);
  }

  .input,
  .textarea {
    background-color: var(--surface-color);
    border-color: var(--border-color);
    color: var(--text-color);

    &::placeholder {
      color: rgba(200, 200, 200, 0.5);
    }
  }

  .button.is-text {
    color: var(--text-color) !important;

    &:hover {
      color: var(--title-color) !important;
    }
  }

  .navbar-dropdown {
    background-color: var(--dropdown-background);
    border-color: var(--border-color);

    .navbar-item {
      color: var(--text-color);

      &:hover {
        background-color: var(--dropdown-item-hover);
      }
    }
  }

  /* リスト要素のダークモード対応 */
  ul,
  ol {
    color: var(--text-color);

    li {
      color: var(--text-color);
    }
  }

  /* ラベルとフィールドのダークモード対応 */
  .label {
    color: var(--text-color) !important;
  }

  .field-label .label {
    color: var(--text-color) !important;
  }

  /* パンくずリストのダークモード対応 */
  .breadcrumb {
    li {
      color: var(--text-color);

      a {
        color: var(--text-color);

        &:hover {
          color: var(--title-color);
        }
      }
    }
  }

  /* タブのダークモード対応 */
  .tabs {
    li {
      a {
        color: var(--text-color);

        &:hover {
          color: var(--title-color);
          border-bottom-color: var(--title-color);
        }
      }

      &.is-active a {
        color: var(--title-color);
        border-bottom-color: var(--primary-color);
      }
    }
  }

  /* セレクトボックスのダークモード対応 */
  .select select {
    background-color: var(--surface-color);
    border-color: var(--border-color);
    color: var(--text-color);

    &:hover {
      border-color: var(--title-color);
    }
  }

  /* showページのダークモード対応 - 強制的に白色に */
  #show {
    color: #FFFFFF !important;

    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    p,
    span,
    div,
    a,
    button {
      color: #FFFFFF !important;
    }

    .file-tag {
      background: rgba(255, 255, 255, 0.1) !important;
      border: 1px solid rgba(255, 255, 255, 0.2) !important;

      &:hover {
        background: rgba(255, 255, 255, 0.15) !important;
      }

      .tag-label,
      .tag-value {
        color: #FFFFFF !important;
      }

      :deep(.b-icon) {
        color: rgba(255, 255, 255, 0.9) !important;
      }
    }

    // 動画変換中のタグ
    .file-tag.video-converting {
      background: rgba(255, 209, 102, 0.1) !important;
      border-color: rgba(255, 209, 102, 0.3) !important;

      .tag-value {
        color: #ffd166 !important;
      }
    }

    // 動画変換完了のタグ
    .file-tag.video-converted {
      background: rgba(40, 167, 69, 0.1) !important;
      border-color: rgba(40, 167, 69, 0.3) !important;

      .tag-value {
        color: #81c784 !important;
      }
    }
  }

  /* タグのダークモード対応 */
  .tag:not(.is-info):not(.is-success):not(.is-warning):not(.is-danger):not(.is-primary) {
    background-color: var(--surface-color);
    color: var(--text-color);
  }

  /* ページネーションのダークモード対応 */
  .pagination-link,
  .pagination-previous,
  .pagination-next {
    background-color: var(--surface-color);
    border-color: var(--border-color);
    color: var(--text-color);

    &:hover {
      background-color: var(--dropdown-item-hover);
      border-color: var(--title-color);
      color: var(--title-color);
    }
  }

  .pagination-ellipsis {
    color: var(--text-color);
  }

  /* フィルタ・ソートコンテナのダークモード対応 */
  .filter-sort-container {
    background-color: var(--surface-color) !important;
    border-color: var(--border-color) !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2) !important;

    .label {
      color: var(--text-color) !important;
    }
  }

  /* フィルタ・ソートボタンのダークモード対応 */
  .filter-buttons .button.is-light,
  .sort-buttons .button.is-light {
    background-color: var(--card-background);
    border-color: var(--border-color);
    color: var(--text-color);

    &:hover {
      background-color: var(--dropdown-item-hover);
      border-color: var(--title-color);
      color: var(--title-color);
    }
  }

  /* Buefyのon-borderラベル付きフィールドのダークモード対応 */
  .field.has-addons .control:not(:last-child) .input,
  .field.has-addons .control:not(:last-child) .taginput .taginput-container {
    border-color: var(--border-color) !important;
  }

  .taginput .taginput-container {
    background-color: var(--surface-color) !important;
    border-color: var(--border-color) !important;

    &.is-focusable:focus,
    &:focus-within {
      border-color: var(--primary-color) !important;
    }
  }

  /* div.group内のinput要素のダークモード対応 */
  .group {
    .field {

      &.has-addons .control .input,
      &.has-addons .control .taginput .taginput-container {
        border-color: var(--border-color) !important;
      }
    }

    /* on-borderラベルのあるフィールド */
    .field-label-is-on-border {

      .input,
      .taginput .taginput-container {
        border-color: var(--border-color) !important;

        &:focus,
        &:focus-within {
          border-color: var(--primary-color) !important;
        }
      }
    }
  }

  /* floating labelの:before要素の背景色を透明に */
  .field.is-floating-label .label:before {
    background-color: transparent !important;
  }

  /* FAQページのダークモード対応 */
  #faq {
    p {
      color: #FFFFFF !important;
    }
  }

  /* サブスクリプションページのダークモード対応 */
  #subscription {

    // 価格表示
    .tile.is-child.box:not(.card-feature) {
      p span {
        color: #FFFFFF !important;
      }
    }

    // ラジオボタンのラベル
    .plan-type-selector .b-radio span {
      color: #FFFFFF !important;
    }

    // 注釈セクションのテキスト
    .annotation {
      color: #FFFFFF !important;

      .pricing-features-item {
        color: #FFFFFF !important;
      }

      .payment-methods {
        color: #FFFFFF !important;
      }
    }

    // Developerカードのタイトル
    .title.has-text-left.has-text-grey-dark {
      color: #FFFFFF !important;
    }

    // ビジネスプランのディスカウントメッセージ
    .tile.is-child.box {
      p.has-text-weight-bold {
        color: #FFFFFF !important;
      }
    }

    // 支払い方法セクション
    .payment-methods-section {
      h3 {
        color: #FFFFFF !important;
      }

      .payment-icon,
      .payment-divider {
        color: #FFFFFF !important;
      }
    }
  }
}

// ページ下部に padding が入るのを防ぐ（fixed-bottom が入れてくる）
@media screen and (max-width: 1023px) {

  html.has-navbar-fixed-bottom-touch,
  body.has-navbar-fixed-bottom-touch {
    padding-bottom: 0 !important;
  }
}

body {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", "Liberation Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji" !important;
  font-weight: 500 !important;
  line-height: 1.8 !important;
  letter-spacing: normal;
  -webkit-font-smoothing: antialiased !important;
  -moz-osx-font-smoothing: grayscale !important;
  color: var(--default-font-color);
  background: var(--background-gradient);
  position: relative;
}

body::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  pointer-events: none;
  background-image:
    radial-gradient(circle at 10% 10%, rgba(73, 116, 187, 0.08) 0%, transparent 30%),
    radial-gradient(circle at 90% 90%, rgba(238, 53, 115, 0.08) 0%, transparent 30%),
    radial-gradient(circle at 50% 50%, rgba(249, 182, 77, 0.05) 0%, transparent 50%);
}

.theme-dark body::before {
  background-image:
    radial-gradient(circle at 10% 10%, rgba(73, 116, 187, 0.03) 0%, transparent 30%),
    radial-gradient(circle at 90% 90%, rgba(238, 53, 115, 0.03) 0%, transparent 30%),
    radial-gradient(circle at 50% 50%, rgba(249, 182, 77, 0.02) 0%, transparent 50%);
}

/* ダークモードでのボディ背景 */
.theme-dark body {
  background: var(--background-gradient) !important;
}

#app {
  text-align: center;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.title {
  color: var(--title-font-color);
}

h1.title {
  line-height: 3rem;
}

h3.title {
  line-height: 3rem;
}

// SP
@media screen and (max-width: 768px) {
  .card-padding {
    padding: 2rem !important;
  }

  .card-padding-mobile {
    padding: 2rem 2rem 0 2rem !important;
  }

  .reverse-columns {
    flex-direction: column-reverse;
    display: flex;
  }

  .margin-top-60px-mobile {
    margin-top: 60px;
  }

  .section-title {
    font-size: 1rem;
  }

  .section-description {
    font-size: 2.1rem;
  }

  #dashboard>div>nav {
    margin-top: 12px;
  }

  // Dashboard のタブを下に固定（モバイル限定）
  nav.tabs {
    bottom: 0;
    position: fixed;
    z-index: 30;
    background-color: var(--surface-color);
    margin-left: -1.5rem;
    height: 60px;
  }

  // ↑ のタブの border を消す
  nav.tabs>ul>li>a {
    border: none;
  }

  // 同上
  // https://dl.hstorage.io/20230529030254_76366d70.png
  nav.tabs>ul {
    width: 100vw;
    border: none;
  }

  // https://dl.hstorage.io/20230529021841_4a6d4c75.png
  .tags.is-centered .tag {
    margin-right: 0 !important;
    margin-left: 0 !important;
  }

  // end Dashboard.vue
  // List.vue スマホでタグがついてるときの余白
  .taginput .taginput-container>.tag,
  .taginput .taginput-container>.tags {
    margin-top: 6px;
    margin-left: 0 !important;
  }

  .label {
    left: 0 !important;
  }

  // end List.vue
  .hide-on-mobile {
    display: none;
  }

  .bottom-navbar {
    margin-right: -6px;
  }

  .my-modal {
    padding: 3rem 1rem;
  }

  // Dashboard でモバイルの画面下ナビバー
  nav.tabs.is-centered.is-fullwidth {
    background-color: var(--navbar-color);
  }

  // Dashboard でモバイルの画面下ナビバーの文字色
  nav.tabs.is-centered.is-fullwidth>ul>li>a {
    color: var(--navbar-link-color) !important;
  }

  // Dashboard でモバイルの画面下ナビバーの文字色
  nav.tabs.is-centered.is-fullwidth>ul>li>a:active {
    color: var(--navbar-link-color) !important;
  }

  // Dashboard でモバイルの画面下ナビバーの文字色
  nav.tabs.is-centered.is-fullwidth>ul>li>a:focus {
    color: var(--navbar-link-color) !important;
  }

  // Dashboard でモバイルの画面下ナビバーの文字色
  nav.tabs.is-centered.is-fullwidth>ul>li>a:hover {
    color: var(--navbar-link-color) !important;
  }
}

@media screen and (min-width: 769px) {
  .card-padding {
    padding: 3.75rem !important;
  }

  .navbar {
    padding: 1rem 10rem;
  }

  .padding-right {
    padding-right: 10rem;
  }

  .section-title {
    font-size: 1rem;
  }

  .section-description {
    font-size: 2.1rem;
  }

  .my-modal {
    padding: 3rem 10rem;
  }
}

@media screen and (min-width: 1024px) {
  .section {
    padding: 3rem 2rem !important;
  }

  .margin-top-60px-desktop {
    margin-top: 60px;
  }

  .section-title {
    font-size: 1.6rem;
  }

  .section-description {
    font-size: 2.5rem;
  }

  .my-modal {
    padding: 3rem 3rem;
  }
}

@media screen and (min-width: 1920px) {}

@media screen and (min-width: 1921px) {
  .navbar {
    padding: 1rem 40rem;
  }

  .section {
    padding: 3rem 10rem !important;
  }

  .padding-right {
    padding-right: 10rem;
  }
}

.container {
  margin: unset !important;
}

/* 特定モーダル（お試しアップロード）を常に最前面に */
.modal.trial-upload-modal,
.modal.trial-upload-modal .modal-background,
.modal.trial-upload-modal .modal-content,
.modal.trial-upload-modal .animation-content,
.modal.trial-upload-modal .modal-card {
  z-index: 10010 !important;
}

/* 配置の崩れ対策: コンテンツを中央寄せ & 適切な横幅に制約 */
.modal.trial-upload-modal .animation-content {
  margin: 20px auto !important;
  max-width: 720px !important;
  width: calc(100% - 40px) !important;
  max-height: calc(100vh - 40px) !important;
  overflow: auto !important;
}

@media screen and (max-width: 768px) {
  .modal.trial-upload-modal .animation-content {
    margin: 12px auto !important;
    max-width: 100% !important;
    width: calc(100% - 24px) !important;
    max-height: calc(100vh - 24px) !important;
  }
}

/* 該当モーダル内部の余白を最適化 */
.modal.trial-upload-modal .my-modal {
  padding: 2rem 1.5rem !important;
  text-align: center !important;
}

@media screen and (min-width: 1024px) {
  .modal.trial-upload-modal .my-modal {
    padding: 2rem 2rem !important;
  }
}

/* お試しモーダル内のボタン/コンテンツを中央配置に補強 */
.modal.trial-upload-modal .content.has-text-centered {
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
}

.modal.trial-upload-modal .content.has-text-centered .button.is-fullwidth {
  width: auto !important;
  min-width: 220px;
}

.modal.trial-upload-modal .content.has-text-centered .button {
  margin: 8px auto 0 !important;
  display: inline-flex;
  justify-content: center;
}

.modal.trial-upload-modal h3,
.modal.trial-upload-modal .trial-modal-desc,
.modal.trial-upload-modal .trial-modal-desc p {
  text-align: center !important;
}

.modal.trial-upload-modal .trial-modal-header {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.cmain {
  margin-top: var(--navbar-height);
  margin-bottom: 60px;
  max-width: 100% !important;
  padding-top: 3.5rem;

  @media screen and (max-width: 1023px) {
    margin-top: var(--navbar-mobile-height);
  }
}

.button-dashboard-bottom {
  position: fixed !important;
  right: 20px !important;
  bottom: 90px !important;
  z-index: 2 !important;
}

.bottom-navbar {
  padding: 0;
  background-color: transparent !important;
  //display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
  text-align: right;
}

.bottom-navbar>.navbar-brand {
  display: none;
}

div.bottom-navbar>div.navbar-menu.is-active {
  background-color: transparent;
  box-shadow: unset;
}

div.navbar-brand {
  min-height: 4.5rem;
}

img {
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  image-rendering: -webkit-optimize-contrast;
}

section.tab-content {
  padding: unset !important;
}

.h2-title {
  margin-bottom: 24px;
}

span.icon.is-right {
  width: 1em !important;
  margin-right: 1em !important;
}

span.icon.is-left {
  width: 1em !important;
  margin-right: 1em !important;
}

.icon svg {
  width: 1em;
  height: 1em;
  max-width: 80%;
  max-height: 80%;
}

.box {
  border-radius: 24px !important;
  background-color: var(--card-background) !important;
}

hr {
  background-color: #4a4a4a;
  text-align: center;
  margin-bottom: 24px;
}

p.break {
  word-break: break-all;
}

.is-primary {
  background-color: $primary !important;
  border-color: $primary !important;
}

.has-text-info {
  color: #004CA3 !important;
}

.has-text-primary {
  color: $primary !important;
}



a.has-text-grey-light:hover {
  color: #b5b5b5 !important;
}

a.has-text-primary:hover {
  color: $primary !important;
}

.has-text-normal {
  color: unset !important;
}

a.has-text-normal:visited {
  color: unset !important;
}

a.has-text-normal:hover {
  color: unset !important;
}

.button.is-info {
  background-color: #222f3d !important;
}

.button.is-primary {
  background-color: $primary !important;
  border-color: $primary !important;
  color: #fff;
}

.button.is-primary:hover {
  color: #fff;
}

.button.is-success {
  background-color: #283149;
}

.button.is-success:hover {
  background-color: #283149;
}

button {
  border-radius: 6px;
}

button.modal-close {
  background-color: var(--border-color);
}

.button.is-small {
  border-radius: 5px;
  font-weight: bold;
}

p.has-text-no-support {
  color: $primary
}

.notices .toast {
  opacity: unset;
}

.navbar-title {
  color: var(--default-font-color);
  font-size: 24px;
  margin: 0 60px 0 0;
}

.navbar-title-sp {
  margin: 0 3rem 0 1rem;
  color: var(--default-font-color);
  font-size: 30px;
}

.navbar {
  padding: 0 1rem;
  background-color: var(--navbar-color) !important;
}

.navbar-burger {
  height: auto !important;
}

a.navbar-item {
  color: var(--navbar-link-color);
}

a.navbar-item.router-link-exact-active.router-link-active {
  color: black;
}

a.navbar-link {
  background-color: transparent;
  color: var(--navbar-link-color);
}

a.navbar-link:active {
  color: var(--navbar-link-color);
}

a.navbar-link:focus {
  color: var(--navbar-link-hover-color);
}

.navbar-link:not(.is-arrowless)::after {
  border-color: var(--navbar-link-color) !important;
}

.navbar-link:not(.is-arrowless):hover::after {
  border-color: var(--navbar-link-hover-color) !important;
}

.navbar-link:not(.is-arrowless):focus::after {
  border-color: var(--navbar-link-hover-color) !important;
}


.section-title {
  display: block;
  font-weight: bold;
  letter-spacing: 0.8px;
  color: $primary;
  margin-bottom: 12px;
}

.section-description {
  font-weight: bold;
  color: var(--title-font-color)
}

.rainbow {
  background: radial-gradient(circle at 50% 50%, rgba(238, 53, 115, 1) 0%, rgba(73, 116, 187, 1) 50%, rgba(249, 182, 77, 1) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.tabs>.item {
  color: white;
  font-size: x-small;
  margin-top: 0.35rem;
  border: none;
}

// SettingComponent
div.b-slider-thumb {
  border-radius: unset !important;
  background-color: white !important;
}

.my-modal {
  border-radius: 30px;
  background-color: var(--modal-background);
  //padding: 60px 12px 60px 12px
}

.fade-enter-active {
  animation-duration: 0.3s !important;
}

.fade-leave-active {
  animation-duration: 0.3s !important;
}

.responsive-image {
  //width: calc(100vw / 3);
  max-width: 256px;
}

.thumb-svg {
  object-fit: cover;
  border-radius: 12px;
  filter: invert(27%) sepia(3%) saturate(128%) hue-rotate(314deg) brightness(92%) contrast(83%);
}

.thumb-img {
  object-fit: cover;
  border-radius: 12px;
}

.original-file-name {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-all;
}

summary {
  outline: none;
}

.margin-top-6px {
  margin-top: 6px
}

.margin-top-12px {
  margin-top: 12px
}

.margin-top-18px {
  margin-top: 18px
}

.margin-top-24px {
  margin-top: 24px
}

.margin-top-30px {
  margin-top: 30px
}

.margin-top-60px {
  margin-top: 60px
}

.margin-bottom-6px {
  margin-bottom: 6px
}

.margin-bottom-12px {
  margin-bottom: 12px
}

.margin-bottom-18px {
  margin-bottom: 18px
}

.margin-bottom-24px {
  margin-bottom: 24px
}

.margin-bottom-30px {
  margin-bottom: 30px
}

.margin-bottom-60px {
  margin-bottom: 60px
}

.padding-top-30px {
  padding-top: 30px;
}

.upload .upload-draggable {
  border: none !important;
}

.pagination-link.is-current {
  color: white !important;
}

// pagination buttons List.vue
nav>a.pagination-link {
  display: none;
}

div.navbar-menu {
  border-bottom-right-radius: 30px;
  border-bottom-left-radius: 30px;
}

div.navbar-dropdown {
  border-bottom-right-radius: 30px;
  border-bottom-left-radius: 30px;
}

// List <b-taginput> オートコンプリート時のサジェストを全て表示するようにする
// default display: block
.autocomplete .dropdown-menu {
  display: contents !important;
}

header.message-header {
  border-radius: 12px 12px 0 0;
}

article.message {
  border-radius: 12px;
}

// b-message のヘッダーを中央にする
header.message-header>p {
  margin: 0 auto;
}

// FAQ
p.accordion__title-text {
  font-weight: bold;
}

div.video-js {
  border-top-right-radius: 12px;
  border-top-left-radius: 12px;
}

video.vjs-tech {
  border-top-right-radius: 12px;
  border-top-left-radius: 12px;
}

// 6) Buefy の CSS はエントリ (src/index.js) で読み込み済み

$animationDuration: 1s;
</style>
